import { Link, useHistory } from "react-router-dom";
import { useEffect, useState, Fragment } from "react";
import { withRouter } from "react-router-dom";
import { Menu, Transition, Dialog } from "@headlessui/react";

import { HiOutlineExclamation, HiOutlineX, HiMenu, HiOutlineUserCircle } from "react-icons/hi";

import Logo from "../images/logo.png";

import { getUserSession as getSession, userSignOut } from "../Common/lib/Session";

/**
 * Returns the Header component.
 *
 * @returns The Header component.
 */
const Header = (props) => {
  const [openModal, setOpenModal] = useState(false);
  const [toggle, setToggle] = useState(true);
  const [success, setSuccess] = useState(false);
  const [profileToggle, setProfileToggle] = useState(false);
  const [userProfile, setUserProfile] = useState({
    fname: "",
    lname: "",
    username: "",
  });
  const history = useHistory();

  const logOut = async () => {
    const { history } = props;

    await userSignOut();

    setSuccess(false);
    if (history) history.push("/");
    window.location.reload();
  };

  /**
    * Gets the user Session information.
    */
  const getUserSession = async () => {
    const session = await getSession();

    if (session.success) {
      setSuccess(true);
      setUserProfile({
        fname: session.fname,
        lname: session.lname,
        username: `${session.fname} `,
      });
    }
  };

  useEffect(() => {
    getUserSession();
  }, [props.updateUser]);

  /**
   * Handles the 'Submit Data' button. If a user has an eligible account, redirects to submit data page. Otherwise shows a modal notifying the user that they need a verified account.
   */
  async function redirectToSubmit() {
    try {
      const result = await getSession();
      const hasContributorAccess =
        result.is_staff ||
        result.contributor_access === true ||
        result.contributor_access === "true";

      if (hasContributorAccess) {
        history.push("/submit-data");
      } else {
        setOpenModal(true);
      }
    } catch (err) {
      // Error handled silently
    }
  }


  /**
   * Checks if localStorage is available
   */
  function storageAvailable(type) {
    var storage;
    try {
      storage = window[type];
      var x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    }
    catch (e) {
      return e instanceof DOMException && (
        // everything except Firefox
        e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        (storage && storage.length !== 0);
    }
  }

  /**
   * Changes the color theme of the website.
   */
  function changeTheme() {
    if (storageAvailable('localStorage')) {
      let currentTheme = localStorage.getItem('color-theme');
      const newTheme = currentTheme === 'light' || currentTheme === null ? 'dark' : 'light';
      localStorage.setItem('color-theme', newTheme);
      window.location.reload();
    }
  }

  /**
   * Modal that appears when an unverified user clicks the 'submit data' button.
   * @returns Modal component
   */
  function VerificationModal() {
    return (
      <Transition.Root show={openModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpenModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 dark:bg-gray-900">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      onClick={() => setOpenModal(false)}
                    >
                      <span className="sr-only">Close</span>
                      <HiOutlineX className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10 dark:bg-yellow-300">
                      <HiOutlineExclamation className="h-6 w-6 text-yellow-600 dark:text-yellow-800" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Verification Required
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          In order to submit your research datasets, you must have a verified contributor account. You can apply for verification in Profile {">"} Account {">"} Contributor Status
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="mt-3 sm:ml-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto dark:border dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:text-white dark:hover:bg-gray-700"
                      onClick={() => setOpenModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sm:mt-0 mt-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:w-auto"
                      onClick={() => { window.location.pathname !== "/profile" ? history.replace("/profile?tab=Account") : setOpenModal(false) }}
                    >
                      Go To Account Settings
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    )
  }

  return (
    <>
      {VerificationModal()}
      <div className="sticky top-0 w-full z-30">
        <div className="relative bg-white shadow-sm border-b dark:bg-gray-900 dark:border-gray-700">
          <div className="max-w-screen-2xl mx-auto px-4 ">
            <div className="flex justify-between items-center lg:py-4 py-2 md:justify-start md:space-x-10">
              <div className="w-0 flex-1 flex">
                <div className="inline-flex">
                  <Link to="/">
                    <img className=" w-24 lg:w-40 dark:invert" src={Logo} alt="Arctic Research Foundation Logo" />
                  </Link>
                </div>
              </div>

              <nav className="hidden lg:flex space-x-10">
                <Link to="/">
                  <div className={"text-gray-700 inline-flex items-center space-x-2 text-base leading-6 hover:text-blue-600 transition ease-in-out duration-150 dark:hover:text-blue-600" + (window.location.pathname === "/" ? " font-bold text-gray-900 dark:text-white" : " dark:text-gray-300 font-medium")}>Home</div>
                </Link>

                <Link to="/about">
                  <div className={"text-base leading-6 text-gray-700 hover:text-blue-600 transition ease-in-out duration-150 dark:hover:text-blue-600" + (window.location.pathname === "/about" ? " font-bold text-gray-900 dark:text-white" : " dark:text-gray-300 font-medium")}>About</div>
                </Link>

                <div className='group relative'>
                  <div className='group/datasets flex items-center cursor-pointer'>
                    <span
                      className={"inline-flex w-full justify-center gap-x-1.5 text-base leading-6 text-gray-700 group-hover/datasets:text-blue-600 transition ease-in-out duration-150 dark:hover:text-blue-600" +
                      (window.location.pathname === "/data-catalogue" || window.location.pathname === "/data-explorer" ? " font-bold text-gray-900 dark:text-white" : " dark:text-gray-300 font-medium")}
                    >
                      Datasets
                    </span>
                    <svg className="-mr-1 h-6 w-6 text-gray-400 group-hover:rotate-180 transition ease-in-out duration-150" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div id="dropdownDivider" className="z-10 hidden group-hover:inline-flex absolute bg-white divide-y divide-gray-100 rounded-lg shadow w-44 dark:bg-gray-700 dark:divide-gray-600">
                    <ul className="py-2 text-sm text-gray-700 dark:text-gray-200 w-full" aria-labelledby="dropdownDividerButton">
                      <li>
                        <Link to="/data-catalogue" className={"block px-4 py-2 hover:bg-gray-100 hover:text-blue-600 dark:hover:text-white dark:hover:bg-gray-600" + (window.location.pathname === "/data-catalogue" ? " font-semibold dark:text-white" : " font-medium dark:text-gray-300")}>
                          Data Catalogue
                        </Link>
                      </li>
                      <li>
                        <Link to="/data-explorer" className={"block px-4 py-2 hover:bg-gray-100 hover:text-blue-600 dark:hover:text-white dark:hover:bg-gray-600" + (window.location.pathname === "/data-explorer" ? " font-semibold text-gray-900 dark:text-white" : " font-medium dark:text-gray-300")}>
                          Data Explorer
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>

                <Link to="/contact">
                  <div className={"text-gray-700 inline-flex items-center space-x-2 text-base leading-6 hover:text-blue-600 transition ease-in-out duration-150 dark:hover:text-blue-600" + (window.location.pathname === "/contact" ? " font-bold text-gray-900 dark:text-white" : " dark:text-gray-300 font-medium")}>Contact Us</div>
                </Link>
              </nav>

              {success ? (
                <div className="hidden md:flex items-center justify-end space-x-4 lg:flex-1 lg:w-0">
                  <button
                    className={"px-4 py-2 border rounded-md border-transparent bg-blue-700 inline-flex items-center text-white leading-6 font-medium text-sm hover:text-blue-600 transition ease-in-out duration-150"}
                    onClick={() => redirectToSubmit()}
                >
                    Submit Data
                </button>

                  <div className="ml-3 relative" data-cy="logged-in">
                    <Menu>
                      {({ open }) => (
                        <>
                          <span className="">
                            <Menu.Button
                              onClick={() =>
                                setProfileToggle(!profileToggle)
                              }
                            >
                              <div className="max-w-xs flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800" id="user-menu" aria-haspopup="true">
                                <HiOutlineUserCircle className="h-9 w-9 text-gray-500 dark:text-gray-400" aria-hidden="true" />

                                <span className="hidden ml-2 text-gray-700 text-sm font-medium lg:block dark:text-white">
                                  <span className="sr-only">Open user menu for </span>
                                  <span className="mr-0">{userProfile.username}</span>
                                </span>

                                <svg className="hidden flex-shrink-0 ml-1 h-5 w-5 text-gray-500 lg:block dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </Menu.Button>
                          </span>

                          {/* Use the Transition + open render prop argument to add transitions. */}
                          <Transition show={open} enter="transition duration-100 ease-out" enterFrom="transform scale-95 opacity-0" enterTo="transform scale-100 opacity-100" leave="transition duration-75 ease-out" leaveFrom="transform scale-100 opacity-100" leaveTo="transform scale-95 opacity-0">
                            <Menu.Items static className="absolute z-30 right-0 w-56 mt-2 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                              <Link to="/profile">
                                <span className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">Profile</span>
                              </Link>

                              <Menu.Item>
                                <div onClick={() => changeTheme()} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer dark:text-white dark:hover:bg-gray-700" role="menuitem">
                                  Change Theme
                                </div>
                              </Menu.Item>

                              <Menu.Item>
                                <div onClick={logOut} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer dark:text-white dark:hover:bg-gray-700" role="menuitem">
                                  Logout
                                </div>
                              </Menu.Item>
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  </div>
                </div>
              ) : (
                <div className="hidden lg:flex items-center justify-end space-x-3 lg:flex-1 lg:w-0">
                  <span className="inline-flex rounded-md shadow-sm lg:w-1/4" style={{ maxWidth: "125px" }}>
                    <Link to="/signin" className="group inline-flex items-center justify-center flex-grow py-2 border-solid border-2 border-blue-700 text-sm leading-6 font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-700 transition ease-in-out duration-150 w-full">
                      <span className="text-blue-700 group-hover:text-white dark:text-white">Log In</span>
                    </Link>
                  </span>
                  <span className="inline-flex rounded-md shadow-sm lg:w-1/4" style={{ maxWidth: "125px" }}>
                    <Link to="/signup" className=" inline-flex items-center justify-center flex-grow py-2 border-solid border-2 border-blue-700 text-sm leading-6 font-medium rounded-md bg-blue-700 hover:border-blue-500 hover:bg-blue-500 focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-700 transition ease-in-out duration-150 w-full">
                      <span className="text-white">Sign Up</span>
                    </Link>
                  </span>
                </div>
              )}


              {/* Mobile Section -------------------------------------------------------*/}
              {success ? (
                <>
                  <div className="flex items-center lg:hidden space-x-3">
                    <button
                      type="button"
                      onClick={redirectToSubmit}
                      className="inline-flex md:hidden items-center px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Submit Data
                    </button>
                    <Menu>
                      {({ open }) => (
                        <>
                            <Menu.Button
                              onClick={() =>
                                setProfileToggle(!profileToggle)
                              }
                            >
                              <div className="float-right inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out dark:hover:bg-gray-800" id="user-menu" aria-haspopup="true">
                                <HiMenu className="h-9 w-9 text-gray-500 dark:text-gray-400" aria-hidden="true" />

                                <span className="hidden ml-3 text-gray-700 text-sm font-medium lg:block">
                                  <span className="sr-only">Open user menu for </span>
                                  <span className="mr-2">{userProfile.fname}</span>
                                  <span className="">{userProfile.lname}</span>
                                </span>
                              </div>
                            </Menu.Button>

                          <Transition show={open} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                            <Menu.Items static className="absolute right-0 w-64 origin-top-left bg-white border border-gray-300 divide-y divide-gray-100 rounded-md shadow-lg outline-none dark:bg-gray-800 dark:border-gray-700">
                            <div className="p-3">
                              <Link to="/" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Home</div>
                              </Link>

                              <Link to="/about" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">About</div>
                              </Link>

                              <Link to="/data-explorer" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Data Explorer</div>
                              </Link>

                              <Link to="/contact" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Support</div>
                              </Link>

                              {/* Account links only on small screens */}
                              <div className="block md:hidden">
                                <div onClick={() => changeTheme()} role="button" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700 cursor-pointer">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Change Theme</div>
                                </div>
                                <Link to="/profile" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Profile</div>
                                </Link>
                                <div onClick={logOut} className="mt-2 w-full cursor-pointer flex items-center justify-center px-4 py-2 text-base leading-6 font-medium rounded text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-700 transition ease-in-out duration-150" role="menuitem">
                                  Logout
                                </div>
                              </div>
                              </div>
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center lg:hidden">
                    <Menu>
                      {({ open }) => (
                        <>
                          <Menu.Button
                            onClick={() =>
                              setToggle(!toggle)
                            }
                          >
                            <div className="float-right inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out dark:hover:bg-gray-800">
                              <HiMenu className="h-9 w-9 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                            </div>
                          </Menu.Button>

                          <Transition show={open} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                            <Menu.Items static className="absolute right-0 w-64 origin-top-left bg-white border border-gray-300 divide-y divide-gray-100 rounded-md shadow-lg outline-none dark:bg-gray-800 dark:border-gray-700">
                              <div className="p-3">
                                <Link to="/" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Home</div>
                                </Link>

                                <Link to="/about" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">About</div>
                                </Link>

                                <Link to="/data-catalogue" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Data Catalogue</div>
                                </Link>

                                <Link to="/contact" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Support</div>
                                </Link>

                                <div onClick={() => changeTheme()} role="button" className="p-3 flex items-center space-x-3 rounded-md hover:bg-gray-50 transition ease-in-out duration-150 dark:hover:bg-gray-700 cursor-pointer">
                                  <div className="text-base leading-6 font-medium text-gray-900 dark:text-white">Change Theme</div>
                                </div>

                                <div className="mt-2 space-y-4">
                                  <span className="w-full flex rounded-md shadow-sm">
                                    <Link to="/signin" className="w-full flex items-center justify-center px-4 py-2 text-base leading-6 font-medium rounded text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:shadow-outline-blue active:bg-blue-700 transition ease-in-out duration-150">
                                      Sign in
                                    </Link>
                                  </span>

                                  <p className="text-center text-base leading-6 font-medium text-gray-500 dark:text-gray-400">
                                    Don't have an account yet?
                                    <Link to="/signup">
                                      <span className="ml-1 text-blue-600 hover:text-blue-500 transition ease-in-out duration-150 dark:text-blue-500">Sign up</span>
                                    </Link>
                                  </p>
                                </div>

                              </div>
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withRouter(Header);